import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Alert, Spin, Tag, Space, Table, Button, Tooltip as AntTooltip, Progress } from 'antd';
import {
  UnorderedListOutlined, TeamOutlined, MailOutlined, SendOutlined,
  EyeOutlined, ArrowUpOutlined, ExclamationCircleOutlined, BarChartOutlined,
  WarningOutlined, ThunderboltOutlined, CloudOutlined, CheckCircleOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area,
} from 'recharts';
import axios from 'axios';

const STATUS_STYLES = {
  draft:     { bg: 'var(--surface-2)', color: 'var(--text-3)' },
  sending:   { bg: 'var(--warn-bg)',   color: 'oklch(0.45 0.14 75)' },
  completed: { bg: 'var(--ok-bg)',     color: 'var(--ok)' },
  scheduled: { bg: 'var(--info-bg)',   color: 'var(--info)' },
};

/* ── Metric card ── */
const StatCard = ({ title, value, icon, iconBg, iconColor, suffix, delta, trend }) => (
  <Card
    style={{ borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}
    bodyStyle={{ padding: '18px 20px' }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <div style={{ color: 'var(--text-3)', fontSize: 11.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
          {title}
        </div>
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 30, fontWeight: 700, color: 'var(--text)', lineHeight: 1, letterSpacing: '-0.04em' }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
          {suffix && <span style={{ fontSize: 14, color: 'var(--text-3)', fontWeight: 400, marginLeft: 4 }}>{suffix}</span>}
        </div>
        {delta != null && (
          <div style={{ marginTop: 6, fontSize: 11.5, fontWeight: 600, color: trend === 'up' ? 'var(--ok)' : 'var(--danger)' }}>
            {trend === 'up' ? '↑' : '↓'} {delta}
          </div>
        )}
      </div>
      <div style={{ width: 42, height: 42, borderRadius: 'var(--r-md)', background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {React.cloneElement(icon, { style: { fontSize: 19, color: iconColor } })}
      </div>
    </div>
  </Card>
);

/* ── Status badge ── */
const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 999, fontSize: 11.5, fontWeight: 600,
      background: s.bg, color: s.color,
    }}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const Dashboard = () => {
  const [stats,         setStats]         = useState(null);
  const [billingStatus, setBillingStatus] = useState(null);
  const [campaigns,     setCampaigns]     = useState([]);
  const [campaignStats, setCampaignStats] = useState({});
  const [timeseries,    setTimeseries]    = useState([]);
  const [error,         setError]         = useState('');
  const [loading,       setLoading]       = useState(true);
  const [resending,     setResending]     = useState(false);
  const [resendMsg,     setResendMsg]     = useState('');
  const navigate = useNavigate();
  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const handleResendVerification = async () => {
    setResending(true);
    setResendMsg('');
    try {
      const res = await axios.post('/api/auth/resend-verification', {}, { headers: authHeader() });
      setResendMsg(res.data.message || 'Verification email sent!');
    } catch (err) {
      setResendMsg(err.response?.data?.message || 'Failed to send — check platform SES configuration.');
    } finally {
      setResending(false);
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      const tok = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${tok}` };
      try {
        const [s, c, b, ts] = await Promise.all([
          axios.get('/api/stats', { headers }),
          axios.get('/api/campaigns', { headers }),
          axios.get('/api/billing/status', { headers }),
          axios.get('/api/stats/timeseries', { headers }),
        ]);
        setStats(s.data);
        setBillingStatus(b.data);
        setTimeseries(ts.data || []);
        const recent = (Array.isArray(c.data) ? c.data : (c.data.data ?? [])).slice(0, 5);
        setCampaigns(recent);

        const nonDraft = recent.filter(camp => camp.status !== 'draft');
        if (nonDraft.length > 0) {
          const results = await Promise.allSettled(
            nonDraft.map(camp => axios.get(`/api/campaigns/${camp.id}/stats`, { headers }))
          );
          const statsMap = {};
          nonDraft.forEach((camp, i) => {
            if (results[i].status === 'fulfilled') statsMap[camp.id] = results[i].value.data;
          });
          setCampaignStats(statsMap);
        }
      } catch {
        setError('Failed to load dashboard data.');
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 100 }}>
      <Spin size="large" />
    </div>
  );

  const MetricPill = ({ value, color, bg, label }) => (
    <AntTooltip title={label}>
      <span style={{
        display: 'inline-flex', alignItems: 'center',
        background: bg, color, borderRadius: 20,
        padding: '2px 8px', fontSize: 11.5, fontWeight: 700,
        fontFamily: 'var(--font-mono)',
      }}>
        {value}
      </span>
    </AntTooltip>
  );

  const recentColumns = [
    {
      title: 'Campaign', dataIndex: 'name', width: 200, ellipsis: true,
      render: (name, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
            {record.subject && <div style={{ fontSize: 11.5, color: 'var(--text-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{record.subject}</div>}
          </div>
          {record.status !== 'draft' && (
            <AntTooltip title="View analytics">
              <Button
                type="text" size="small" icon={<BarChartOutlined />}
                onClick={() => navigate(`/campaigns/${record.id}/report`)}
                style={{ color: 'var(--brand-primary)', padding: '0 4px', height: 22, flexShrink: 0 }}
              />
            </AntTooltip>
          )}
        </div>
      ),
    },
    {
      title: 'Status', dataIndex: 'status', width: 110,
      render: s => <StatusBadge status={s} />,
    },
    {
      title: 'Sent', key: 'sent', width: 70, align: 'right', responsive: ['sm'],
      render: (_, r) => {
        const s = campaignStats[r.id];
        if (!s) return <span style={{ color: 'var(--text-3)', fontSize: 12 }}>—</span>;
        return <MetricPill value={s.total?.toLocaleString()} color="var(--text)" bg="var(--surface-2)" label="Total emails sent" />;
      },
    },
    {
      title: 'Open %', key: 'openRate', width: 85, align: 'right',
      render: (_, r) => {
        const s = campaignStats[r.id];
        if (!s) return <span style={{ color: 'var(--text-3)', fontSize: 12 }}>—</span>;
        return <MetricPill value={`${s.openRate}%`} color="var(--ok)" bg="var(--ok-bg)" label={`${(s.opened || 0) + (s.clicked || 0)} opened`} />;
      },
    },
    {
      title: 'Click %', key: 'clickRate', width: 85, align: 'right', responsive: ['md'],
      render: (_, r) => {
        const s = campaignStats[r.id];
        if (!s) return <span style={{ color: 'var(--text-3)', fontSize: 12 }}>—</span>;
        return <MetricPill value={`${s.clickRate}%`} color="var(--info)" bg="var(--info-bg)" label={`${s.clicked || 0} clicked`} />;
      },
    },
    {
      title: 'Bounce %', key: 'bounceRate', width: 90, align: 'right', responsive: ['md'],
      render: (_, r) => {
        const s = campaignStats[r.id];
        if (!s) return <span style={{ color: 'var(--text-3)', fontSize: 12 }}>—</span>;
        const col = parseFloat(s.bounceRate) > 5 ? 'var(--danger)' : parseFloat(s.bounceRate) > 2 ? 'oklch(0.45 0.14 75)' : 'var(--text-2)';
        const bg  = parseFloat(s.bounceRate) > 5 ? 'var(--danger-bg)' : parseFloat(s.bounceRate) > 2 ? 'var(--warn-bg)' : 'var(--surface-2)';
        return <MetricPill value={`${s.bounceRate}%`} color={col} bg={bg} label={`${s.bounced || 0} bounced`} />;
      },
    },
    {
      title: 'Created', dataIndex: 'created_at', width: 110, responsive: ['lg'],
      render: d => <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>,
    },
  ];

  return (
    <div style={{ maxWidth: 1200, width: '100%' }}>

      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 20, borderRadius: 'var(--r-md)' }} />}

      {/* Status banners */}
      {billingStatus && !billingStatus.email_verified && (
        <Alert
          type="warning" showIcon icon={<MailOutlined />}
          message="Verify your email address"
          description={
            <span>
              Please check your inbox for the verification link. You won't be able to send campaigns until your email is verified.
              {resendMsg && (
                <span style={{ display: 'block', marginTop: 4, fontWeight: 500, color: resendMsg.includes('Failed') ? 'var(--danger)' : 'var(--ok)' }}>
                  {resendMsg}
                </span>
              )}
            </span>
          }
          action={
            <Space>
              <Button size="small" loading={resending} onClick={handleResendVerification}>
                Resend Email
              </Button>
              <Button size="small" onClick={() => navigate('/settings')}>Settings</Button>
            </Space>
          }
          style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }}
        />
      )}
      {billingStatus && billingStatus.email_verified && !billingStatus.ses_verified && (
        <Alert
          type="warning" showIcon icon={<CloudOutlined />}
          message="Connect your AWS SES account"
          description="You need to connect AWS SES before you can send campaigns. It only takes 2 minutes."
          action={<Button size="small" type="primary" onClick={() => navigate('/settings#ses')}>Connect SES</Button>}
          style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }}
        />
      )}
      {billingStatus && billingStatus.plan === 'pro' && billingStatus.ses_verified && billingStatus.email_verified && campaigns.length === 0 && (
        <Alert
          type="success" showIcon icon={<CheckCircleOutlined />}
          message="You're all set!"
          description="Your Pro account is active and AWS SES is connected. Create your first campaign to get started."
          action={<Button size="small" type="primary" onClick={() => navigate('/campaigns/create')}>Create Campaign</Button>}
          style={{ marginBottom: 16, borderRadius: 'var(--r-md)' }}
          closable
        />
      )}

      {/* Free plan usage */}
      {billingStatus && billingStatus.plan === 'free' && (
        <Card
          style={{
            marginBottom: 20, borderRadius: 'var(--r-lg)',
            border: billingStatus.campaigns_remaining === 0 ? '1px solid oklch(0.85 0.08 25)' : '1px solid oklch(0.88 0.08 75)',
            background: billingStatus.campaigns_remaining === 0 ? 'var(--danger-bg)' : 'var(--warn-bg)',
            boxShadow: 'none',
          }}
          bodyStyle={{ padding: '16px 20px' }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <WarningOutlined style={{ color: billingStatus.campaigns_remaining === 0 ? 'var(--danger)' : 'oklch(0.45 0.14 75)' }} />
                <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>
                  {billingStatus.campaigns_remaining === 0 ? 'Monthly campaign limit reached' : 'Campaigns used this month'}
                </span>
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginBottom: 10 }}>
                {billingStatus.campaigns_remaining === 0
                  ? `You've used all ${billingStatus.campaigns_allowed} campaign${billingStatus.campaigns_allowed === 1 ? '' : 's'} on your Free plan this month. Upgrade for unlimited campaigns.`
                  : `Your Free plan includes ${billingStatus.campaigns_allowed} campaign${billingStatus.campaigns_allowed === 1 ? '' : 's'} per month. Upgrade to Pro for unlimited.`}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Progress
                  percent={Math.min(100, Math.round((billingStatus.campaigns_this_month / (billingStatus.campaigns_allowed || 1)) * 100))}
                  strokeColor={billingStatus.campaigns_remaining === 0 ? '#dc2626' : '#d97706'}
                  trailColor="rgba(0,0,0,0.08)" showInfo={false}
                  style={{ flex: 1 }} strokeWidth={6}
                />
                <span style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', color: 'var(--text-2)' }}>
                  {billingStatus.campaigns_this_month} of {billingStatus.campaigns_allowed} campaign{billingStatus.campaigns_allowed === 1 ? '' : 's'}
                </span>
              </div>
            </div>
            <Button
              type="primary" icon={<ThunderboltOutlined />}
              onClick={() => navigate('/billing')}
              style={{ borderRadius: 'var(--r-md)', fontWeight: 600, flexShrink: 0 }}
            >
              Upgrade to Pro
            </Button>
          </div>
        </Card>
      )}

      {/* Stat cards — 2×2 on mobile, 4-in-a-row on xl */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={12} sm={12} xl={6}>
          <StatCard title="Audiences" value={stats?.lists ?? 0} icon={<UnorderedListOutlined />} iconBg="var(--brand-tint)" iconColor="var(--brand-primary)" />
        </Col>
        <Col xs={12} sm={12} xl={6}>
          <StatCard title="Subscribers" value={stats?.subscribers ?? 0} icon={<TeamOutlined />} iconBg="var(--ok-bg)" iconColor="var(--ok)" />
        </Col>
        <Col xs={12} sm={12} xl={6}>
          <StatCard title="Campaigns" value={stats?.campaigns ?? 0} icon={<MailOutlined />} iconBg="var(--warn-bg)" iconColor="oklch(0.45 0.14 75)" />
        </Col>
        <Col xs={12} sm={12} xl={6}>
          <StatCard title="Emails Sent" value={stats?.sent ?? 0} icon={<SendOutlined />} iconBg="var(--info-bg)" iconColor="var(--info)" />
        </Col>
      </Row>

      {/* Engagement row */}
      {stats && (stats.opens > 0 || stats.clicks > 0 || stats.bounces > 0) && (
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          <Col xs={24} sm={8}>
            <StatCard title="Total Opens" value={stats.opens} icon={<EyeOutlined />} iconBg="var(--ok-bg)" iconColor="var(--ok)" />
          </Col>
          <Col xs={24} sm={8}>
            <StatCard title="Total Clicks" value={stats.clicks} icon={<ArrowUpOutlined />} iconBg="var(--info-bg)" iconColor="var(--info)" />
          </Col>
          <Col xs={24} sm={8}>
            <StatCard title="Bounces" value={stats.bounces} icon={<ExclamationCircleOutlined />} iconBg="var(--danger-bg)" iconColor="var(--danger)" />
          </Col>
        </Row>
      )}

      {/* Chart + Quick stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} lg={16}>
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontWeight: 650, fontSize: 15, color: 'var(--text)' }}>30-Day Engagement</span>
                <Space size={12}>
                  <Space size={4}><div style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--ok)' }} /><span style={{ fontSize: 12, color: 'var(--text-3)' }}>Opens</span></Space>
                  <Space size={4}><div style={{ width: 8, height: 8, borderRadius: 2, background: 'var(--brand-primary)' }} /><span style={{ fontSize: 12, color: 'var(--text-3)' }}>Clicks</span></Space>
                </Space>
              </div>
            }
            style={{ borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}
            bodyStyle={{ padding: '12px 20px 20px' }}
          >
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={timeseries} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="colorOpens"  x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#059669" stopOpacity={0.12} />
                    <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%"  stopColor="#0a40d9" stopOpacity={0.12} />
                    <stop offset="95%" stopColor="#0a40d9" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                <XAxis
                  dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-3)' }}
                  axisLine={false} tickLine={false}
                  interval={Math.floor(timeseries.length / 6)}
                />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--text-3)' }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{ borderRadius: 'var(--r-md)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-md)', fontSize: 12 }}
                  labelStyle={{ fontWeight: 600, marginBottom: 4, color: 'var(--text)' }}
                />
                <Area type="monotone" dataKey="opens"  name="Opens"  stroke="#059669" strokeWidth={1.5} fill="url(#colorOpens)"  dot={false} activeDot={{ r: 4 }} />
                <Area type="monotone" dataKey="clicks" name="Clicks" stroke="#0a40d9" strokeWidth={1.5} fill="url(#colorClicks)" dot={false} activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            title={<span style={{ fontWeight: 650, fontSize: 15, color: 'var(--text)' }}>Sender Reputation</span>}
            style={{ borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)', height: '100%' }}
            bodyStyle={{ padding: '16px 20px' }}
          >
            {stats ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {[
                  {
                    label: 'Open Rate',
                    value: stats.sent > 0 ? ((stats.opens / stats.sent) * 100).toFixed(1) + '%' : '—',
                    color: 'var(--ok)', bg: 'var(--ok-bg)',
                    pct: stats.sent > 0 ? Math.min(100, (stats.opens / stats.sent) * 100) : 0,
                    barColor: '#059669',
                  },
                  {
                    label: 'Click Rate',
                    value: stats.sent > 0 ? ((stats.clicks / stats.sent) * 100).toFixed(1) + '%' : '—',
                    color: 'var(--info)', bg: 'var(--info-bg)',
                    pct: stats.sent > 0 ? Math.min(100, (stats.clicks / stats.sent) * 100) : 0,
                    barColor: '#0a40d9',
                  },
                  {
                    label: 'Bounce Rate',
                    value: stats.sent > 0 ? ((stats.bounces / stats.sent) * 100).toFixed(1) + '%' : '—',
                    color: stats.sent > 0 && (stats.bounces / stats.sent) > 0.05 ? 'var(--danger)' : 'var(--text-2)',
                    bg: 'var(--surface-2)',
                    pct: stats.sent > 0 ? Math.min(100, (stats.bounces / stats.sent) * 100 * 2) : 0,
                    barColor: '#dc2626',
                  },
                ].map(({ label, value, color, pct, barColor }) => (
                  <div key={label}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12.5, color: 'var(--text-2)', fontWeight: 500 }}>{label}</span>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color }}>{value}</span>
                    </div>
                    <Progress
                      percent={pct} showInfo={false}
                      strokeColor={barColor} trailColor="var(--border)"
                      strokeWidth={5} style={{ margin: 0 }}
                    />
                  </div>
                ))}

                <div
                  onClick={() => navigate('/deliverability')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
                    background: 'var(--brand-tint)', borderRadius: 'var(--r-md)',
                    cursor: 'pointer', marginTop: 4,
                    border: '1px solid var(--brand-tint-2)',
                  }}
                >
                  <SafetyCertificateOutlined style={{ color: 'var(--brand-primary)', fontSize: 14 }} />
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--brand-primary)' }}>View Deliverability Report</span>
                </div>
              </div>
            ) : (
              <span style={{ color: 'var(--text-3)' }}>No data available</span>
            )}
          </Card>
        </Col>
      </Row>

      {/* Recent campaigns */}
      {campaigns.length > 0 && (
        <Card
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontWeight: 650, fontSize: 15, color: 'var(--text)' }}>Recent Campaigns</span>
              <Button type="link" size="small" onClick={() => navigate('/campaigns')} style={{ color: 'var(--brand-primary)', fontWeight: 600, padding: 0 }}>
                View all
              </Button>
            </div>
          }
          style={{ borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', boxShadow: 'var(--shadow-sm)' }}
          bodyStyle={{ padding: '0 0 4px' }}
        >
          <Table
            dataSource={campaigns}
            columns={recentColumns}
            rowKey="id"
            pagination={false}
            size="small"
            scroll={{ x: 'max-content' }}
            style={{ borderRadius: 'var(--r-lg)' }}
          />
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
